// src/pages/ppic/UoMConfig.jsx
import React, { useState, useRef, useEffect } from 'react';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
    Typography, Box,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, IconButton, Stack, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, Button, Snackbar, Alert,
    CircularProgress, Menu, MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from '@mui/icons-material/Close';

import { database } from "../../firebase";
import { ref, push, set, onValue, update, remove } from "firebase/database";

// Helper: ubah onValue menjadi Promise
const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

function UoMConfig({ title, dbPath, fieldName, primaryKeyField }) {
    const [dataList, setDataList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [openDialog, setOpenDialog] = useState(false);
    const [fieldValue, setFieldValue] = useState("");
    const [deskripsiValue, setDeskripsiValue] = useState(""); // State baru untuk deskripsi
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [currentId, setCurrentId] = useState(null);

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });

    const fileInputRef = useRef(null);

    // Ambil data dari Firebase
    useEffect(() => {
        const tbRef = ref(database, dbPath);
        setLoading(true);

        const unsubscribe = onValue(
            tbRef,
            (snapshot) => {
                const data = snapshot.val();
                if (snapshot.exists()) {
                    const list = Object.keys(data).map(key => ({
                        [primaryKeyField]: key,
                        ...data[key]
                    }));
                    setDataList(list);
                } else {
                    setDataList([]);
                }
                setLoading(false);
            },
            (error) => {
                console.error("Error:", error);
                setSnackbar({
                    open: true,
                    message: `Gagal memuat data ${title.toLowerCase()}.`,
                    severity: "error",
                });
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [dbPath, title, primaryKeyField]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleOpenDialog = () => {
        setIsEditing(false);
        setFieldValue("");
        setDeskripsiValue(""); // Reset deskripsi
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setFieldValue("");
        setDeskripsiValue(""); // Reset deskripsi
        setIsEditing(false);
        setCurrentId(null);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleSave = async () => {
        if (!fieldValue.trim()) {
            setSnackbar({
                open: true,
                message: `${title.slice(title.indexOf(" ") + 1)} tidak boleh kosong.`,
                severity: "warning",
            });
            return;
        }

        const normalizedFieldValue = fieldValue.toLowerCase().trim();

        const isDuplicate = dataList.some(item =>
            item[fieldName]?.toLowerCase().trim() === normalizedFieldValue &&
            item[primaryKeyField] !== currentId
        );

        if (isDuplicate) {
            setSnackbar({
                open: true,
                message: `${title.slice(title.indexOf(" ") + 1)} "${fieldValue}" sudah ada.`,
                severity: "error",
            });
            return;
        }

        try {
            if (isEditing) {
                const itemRef = ref(database, `${dbPath}/${currentId}`);
                const updatedData = { 
                    [fieldName]: fieldValue,
                    ...(title === "Daftar Kode Material" && { deskripsi_material: deskripsiValue })
                };
                await update(itemRef, updatedData);
                setSnackbar({
                    open: true,
                    message: `${title.slice(title.indexOf(" ") + 1)} berhasil diperbarui.`,
                    severity: "success",
                });
            } else {
                const tbRef = ref(database, dbPath);
                const newRef = push(tbRef);
                const key = newRef.key;
                const data = {
                    [primaryKeyField]: key,
                    [fieldName]: fieldValue,
                    ...(title === "Daftar Kode Material" && { deskripsi_material: deskripsiValue })
                };
                await set(newRef, data);
                setSnackbar({
                    open: true,
                    message: `${title.slice(title.indexOf(" ") + 1)} berhasil disimpan.`,
                    severity: "success",
                });
            }
            handleCloseDialog();
        } catch (error) {
            console.error("Gagal simpan/update:", error);
            setSnackbar({
                open: true,
                message: `Gagal menyimpan/memperbarui data ${title.toLowerCase()}.`,
                severity: "error",
            });
        }
    };

    const handleImportButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleImportExcel = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: "array" });

                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet);

                const tbRef = ref(database, dbPath);

                const snapshot = await onValuePromise(tbRef);
                const existingData = snapshot.exists() ? snapshot.val() : {};

                const existingFieldValues = Object.values(existingData).map(item => item[fieldName]?.toLowerCase().trim());

                let importedCount = 0;
                let ignoredCount = 0;

                for (const item of jsonData) {
                    let importedValue = item["Kode Material"]; // Sesuai dengan header Excel
                    let importedDeskripsi = item["Deskripsi Material"] || ""; // Ambil deskripsi dari Excel

                    if (!importedValue) {
                        ignoredCount++;
                        continue;
                    }

                    const valueLower = importedValue.toLowerCase().trim();
                    if (existingFieldValues.includes(valueLower)) {
                        ignoredCount++;
                        continue;
                    }
                    
                    const dataToSave = { 
                        [fieldName]: importedValue,
                        ...(title === "Daftar Kode Material" && { deskripsi_material: importedDeskripsi })
                    };
                    
                    const newRef = push(tbRef);
                    const key = newRef.key;
                    dataToSave[primaryKeyField] = key;
                    await set(newRef, dataToSave);
                    importedCount++;
                }

                setSnackbar({
                    open: true,
                    message: `Berhasil mengimpor ${importedCount} data baru. ${ignoredCount} data yang sudah ada/tidak valid diabaikan.`,
                    severity: "success",
                });
            } catch (error) {
                console.error("Gagal mengimpor Excel:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal mengimpor data dari Excel.",
                    severity: "error",
                });
            } finally {
                event.target.value = null;
            }
        };

        reader.readAsArrayBuffer(file);
    };

    const exportToExcel = () => {
        if (!dataList || dataList.length === 0) {
            setSnackbar({
                open: true,
                message: "Tidak ada data untuk diekspor.",
                severity: "warning",
            });
            return;
        }

        const formattedData = dataList.map((item) => {
            const row = {};
            row[primaryKeyField] = item[primaryKeyField];
            
            if (title === "Daftar Kode Material") {
                row["Kode Material"] = item[fieldName];
                row["Deskripsi Material"] = item.deskripsi_material || "";
            } else {
                row[title.slice(title.indexOf(" ") + 1)] = item[fieldName];
            }
            
            return row;
        });

        const worksheet = XLSX.utils.json_to_sheet(formattedData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, title.replace("Daftar ", ""));

        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/octet-stream" });

        saveAs(blob, `${title.replace("Daftar ", "Data_")}.xlsx`);
    };

    const handleMenuClick = (event, row) => {
        setMenuAnchor(event.currentTarget);
        setSelectedRow(row);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
        setSelectedRow(null);
    };

    const handleEdit = (row) => {
        handleMenuClose();
        setIsEditing(true);
        setCurrentId(row[primaryKeyField]);
        setFieldValue(row[fieldName]);
        setDeskripsiValue(row.deskripsi_material || ""); // Set nilai deskripsi
        setOpenDialog(true);
    };

    const handleDelete = (row) => {
        handleMenuClose();
        setSelectedRow(row);
        setOpenDeleteDialog(true);
    };

    const confirmDelete = async () => {
        if (!selectedRow) return;

        try {
            const idToDelete = selectedRow[primaryKeyField];
            const itemRef = ref(database, `${dbPath}/${idToDelete}`);
            await remove(itemRef);
            setSnackbar({
                open: true,
                message: `${title.slice(title.indexOf(" ") + 1)} berhasil dihapus.`,
                severity: "success",
            });
            setOpenDeleteDialog(false);
            setSelectedRow(null);
        } catch (error) {
            console.error("Gagal menghapus:", error);
            setSnackbar({
                open: true,
                message: `Gagal menghapus data ${title.toLowerCase()}.`,
                severity: "error",
            });
        }
    };

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
        setSelectedRow(null);
    };

    // Fungsi untuk menentukan header kolom berdasarkan jenis data
    const getTableHeaders = () => {
        if (title === "Daftar Kode Material") {
            return ["Aksi", "Id", "Kode Material", "Deskripsi Material"];
        } else {
            return ["Aksi", "Id", title.slice(title.indexOf(" ") + 1)];
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'inherit' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="h6">{title}</Typography>
                <Stack direction="row" spacing={1}>
                    <IconButton color="primary" size="small" onClick={handleOpenDialog}>
                        <AddIcon />
                    </IconButton>
                    <IconButton color="primary" size="small" onClick={handleImportButtonClick}>
                        <FileUploadOutlinedIcon />
                    </IconButton>
                    <input
                        type="file"
                        accept=".xlsx, .xls"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        onChange={handleImportExcel}
                    />
                    <IconButton color="primary" size="small" onClick={exportToExcel}>
                        <FileDownloadOutlinedIcon />
                    </IconButton>
                </Stack>
            </Stack>

            <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            {getTableHeaders().map((header, index) => (
                                <TableCell key={index}>{header}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {dataList.length > 0 ? (
                            dataList
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row) => (
                                    <TableRow key={row[primaryKeyField]}>
                                        <TableCell>
                                            <IconButton size="small" onClick={(event) => handleMenuClick(event, row)}>
                                                <MoreVertIcon />
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>{row[primaryKeyField]}</TableCell>
                                        <TableCell>{row[fieldName]}</TableCell>
                                        {/* Tambahkan kolom Deskripsi Material jika ini adalah Daftar Kode Material */}
                                        {title === "Daftar Kode Material" && (
                                            <TableCell>
                                                {row.deskripsi_material || "-"}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={getTableHeaders().length} align="center">
                                    Tidak ada data {title.slice(title.indexOf(" ") + 1).toLowerCase()}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={() => handleEdit(selectedRow)}>
                    <EditIcon fontSize="small" sx={{ mr: 2 }} />
                    Edit
                </MenuItem>
                <MenuItem onClick={() => handleDelete(selectedRow)}>
                    <DeleteIcon fontSize="small" sx={{ mr: 2 }} />
                    Hapus
                </MenuItem>
            </Menu>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
                <Typography variant="body2">
                    {`${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, dataList.length)} dari ${dataList.length}`}
                </Typography>
                <TablePagination
                    component="div"
                    count={dataList.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 20]}
                    labelRowsPerPage=""
                    labelDisplayedRows={() => ""}
                    sx={{
                        ".MuiTablePagination-toolbar": {
                            padding: 0,
                            minHeight: "auto",
                        },
                        ".MuiTablePagination-actions": {
                            display: "block",
                        },
                        ".MuiTablePagination-selectLabel": {
                            display: "none",
                        },
                        ".MuiTablePagination-displayedRows": {
                            display: "none",
                        },
                        ".MuiTablePagination-select": {
                            marginLeft: "8px",
                            marginRight: "8px",
                        },
                    }}
                />
            </Box>

            {/* Dialog Tambah/Edit */}
            <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="xs">
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
                    {isEditing ? `Edit ${title.slice(title.indexOf(" ") + 1)}` : `Tambah ${title.slice(title.indexOf(" ") + 1)}`}
                    <IconButton aria-label="close" onClick={handleCloseDialog}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={title === "Daftar Kode Material" ? "Kode Material" : title.slice(title.indexOf(" ") + 1)}
                        type="text"
                        fullWidth
                        value={fieldValue}
                        onChange={(e) => setFieldValue(e.target.value)}
                        sx={{ mb: 2 }}
                    />
                    {/* Tambahkan field Deskripsi Material hanya untuk Kode Material */}
                    {title === "Daftar Kode Material" && (
                        <TextField
                            margin="dense"
                            label="Deskripsi Material"
                            type="text"
                            fullWidth
                            multiline
                            rows={3}
                            value={deskripsiValue}
                            onChange={(e) => setDeskripsiValue(e.target.value)}
                        />
                    )}
                </DialogContent>
                <DialogActions sx={{ pt: "20px", pb: "20px", pl: "24px", pr: "24px" }}>
                    <Button onClick={handleCloseDialog}>Batal</Button>
                    <Button onClick={handleSave} variant="contained">
                        {isEditing ? "Perbarui" : "Simpan"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog Konfirmasi Hapus Data Tabel Utama */}
            <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog} maxWidth="xs" fullWidth>
                <DialogTitle>Konfirmasi Hapus</DialogTitle>
                <DialogContent>
                    <Typography>
                        Apakah Anda yakin ingin menghapus {title.slice(title.indexOf(" ") + 1).toLowerCase()} "{selectedRow?.[fieldName]}"?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ pt: "0px", pb: "20px", pl: "24px", pr: "24px" }}>
                    <Button onClick={handleCloseDeleteDialog}>Batal</Button>
                    <Button
                        onClick={confirmDelete}
                        variant="contained"
                        color="error"
                    >
                        Hapus
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default UoMConfig;